import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Key, Globe, Lock, Copy, Check, Plus, Trash2, AlertTriangle, RefreshCw, Settings2, Network, CheckCircle2, XCircle, Loader2, Bot } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useBranding } from "@/hooks/use-branding";
import { useFeature } from "@/lib/auth";

// Mirrors the server-side SsoAttributeMapping in server/sso.ts. Keep in sync
// when adding new mappable claims (e.g. `manager`).
type SsoAttributeMapping = {
  email?: string;
  name?: string;
  employeeId?: string;
  department?: string;
  jobTitle?: string;
  role?: string;
  manager?: string;
};

type SsoConfig = {
  provider?: "oidc" | "saml" | "test";
  oidc?: {
    issuer?: string;
    clientId?: string;
    clientSecret?: string;
    scopes?: string;
    attributeMapping?: SsoAttributeMapping;
  };
  saml?: {
    entryPoint?: string;
    issuer?: string;
    cert?: string;
    wantAssertionsSigned?: boolean;
    attributeMapping?: SsoAttributeMapping;
  };
  attributeMapping?: SsoAttributeMapping;
  defaultRole?: string;
  autoProvision?: boolean;
  // roleMapping: IdP claim value → internal role name.
  roleMapping?: Record<string, string>;
};

type SsoResponse = {
  mode: string;
  ssoEnforced: string;
  config: SsoConfig;
  callbackUrls: { oidc: string; saml: string; loginUrl: string };
};

type ScimToken = { id: string; label: string; lastUsedAt: string | null; createdAt: string };
type ScimConfig = { defaultRole: string | null; groupAttributeMapping: string | null; domainRestriction: string | null };

function CopyField({ value, label, testId }: { value: string; label: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <Label className="text-xs" htmlFor={testId}>{label}</Label>
      <div className="flex gap-2">
        <Input readOnly id={testId} value={value} className="font-mono text-xs" data-testid={testId} />
        <IconButton
          variant="outline"
          label={`Copy ${label}`}
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
        </IconButton>
      </div>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface BotListItem { id: string; name: string; }

interface ExternalIdPResponse {
  id: string;
  chatbotId: string;
  providerType: string;
  enabled: boolean;
  issuer?: string;
  audience?: string;
  clientId?: string;
  jwksUri?: string;
  metadataUrl?: string;
  claimMappingJson?: string;
}

interface ClaimMapping {
  userIdClaimName: string;
  emailClaimName: string;
  nameClaimName: string;
  roleClaimName: string;
  departmentClaimName: string;
  groupsClaimName: string;
  // SAML-only extras (stored in claimMappingJson)
  samlSsoUrl?: string;
  samlCert?: string;
}

const DEFAULT_CLAIM_MAPPING: ClaimMapping = {
  userIdClaimName:     "sub",
  emailClaimName:      "email",
  nameClaimName:       "name",
  roleClaimName:       "roles",
  departmentClaimName: "department",
  groupsClaimName:     "groups",
};

function parseClaimMapping(json?: string): ClaimMapping {
  if (!json) return { ...DEFAULT_CLAIM_MAPPING };
  try {
    const parsed = JSON.parse(json);
    return { ...DEFAULT_CLAIM_MAPPING, ...parsed };
  } catch {
    return { ...DEFAULT_CLAIM_MAPPING };
  }
}

function serializeClaimMapping(m: ClaimMapping): string {
  return JSON.stringify(m);
}

// ── ExternalSsoSection ────────────────────────────────────────────────────────
// Per-chatbot external OIDC/SAML identity-provider configuration.
// Completely separate from org-level TalentIQ SSO above — does not touch any
// existing state/API calls.

function ExternalSsoSection() {
  const { toast } = useToast();

  // ── Bot list ────────────────────────────────────────────────────────────────
  const { data: bots = [], isLoading: botsLoading } =
    useQuery<BotListItem[]>({ queryKey: ["/api/chatbots"] });

  const [selectedBotId, setSelectedBotId] = useState<string>("");

  // ── Per-bot IdP config fetch ────────────────────────────────────────────────
  const idpKey = selectedBotId ? [`/api/chatbots/${selectedBotId}/external-idp`] : null;
  const {
    data: idpData,
    isLoading: idpLoading,
    error: idpError,
  } = useQuery<ExternalIdPResponse>({
    queryKey: idpKey ?? ["__noop__"],
    enabled: !!selectedBotId,
    retry: false,
  });

  // ── Form state ──────────────────────────────────────────────────────────────
  const [enabled,      setEnabled]      = useState(true);
  const [providerType, setProviderType] = useState<"oidc" | "saml">("oidc");

  // OIDC
  const [issuer,      setIssuer]      = useState("");
  const [audience,    setAudience]    = useState("");
  const [clientId,    setClientId]    = useState("");
  const [jwksUri,     setJwksUri]     = useState("");

  // SAML
  const [metadataUrl, setMetadataUrl] = useState("");
  const [samlSsoUrl,  setSamlSsoUrl]  = useState("");
  const [samlCert,    setSamlCert]    = useState("");
  // For SAML: Entity ID reuses `issuer`, SSO URL stored in claim mapping JSON.

  // Claim mapping
  const [claims, setClaims] = useState<ClaimMapping>({ ...DEFAULT_CLAIM_MAPPING });

  // Test connection result
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testPending, setTestPending] = useState(false);

  // ── Hydrate form when fetched data changes ─────────────────────────────────
  useEffect(() => {
    if (!selectedBotId) {
      // Reset to defaults when bot deselected
      setEnabled(true);
      setProviderType("oidc");
      setIssuer(""); setAudience(""); setClientId(""); setJwksUri("");
      setMetadataUrl(""); setSamlSsoUrl(""); setSamlCert("");
      setClaims({ ...DEFAULT_CLAIM_MAPPING });
      setTestResult(null);
      return;
    }

    // 404 = no config yet → keep defaults (form is ready for first-time save)
    const is404 = idpError && (idpError as { status?: number }).status === 404;
    if (is404 || !idpData) return;

    const parsed = parseClaimMapping(idpData.claimMappingJson);
    setEnabled(idpData.enabled);
    setProviderType((idpData.providerType ?? "oidc") as "oidc" | "saml");
    setIssuer(idpData.issuer ?? "");
    setAudience(idpData.audience ?? "");
    setClientId(idpData.clientId ?? "");
    setJwksUri(idpData.jwksUri ?? "");
    setMetadataUrl(idpData.metadataUrl ?? "");
    setSamlSsoUrl(parsed.samlSsoUrl ?? "");
    setSamlCert(parsed.samlCert ?? "");
    setClaims({
      userIdClaimName:     parsed.userIdClaimName,
      emailClaimName:      parsed.emailClaimName,
      nameClaimName:       parsed.nameClaimName,
      roleClaimName:       parsed.roleClaimName,
      departmentClaimName: parsed.departmentClaimName,
      groupsClaimName:     parsed.groupsClaimName,
    });
    setTestResult(null);
  }, [selectedBotId, idpData, idpError]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const buildPayload = () => ({
    providerType,
    enabled,
    issuer:      providerType === "oidc" ? issuer    : issuer,   // SAML: entity ID
    audience:    providerType === "oidc" ? audience  : undefined,
    clientId:    providerType === "oidc" ? clientId  : undefined,
    jwksUri:     providerType === "oidc" ? jwksUri   : undefined,
    metadataUrl: metadataUrl || undefined,
    claimMappingJson: serializeClaimMapping({
      ...claims,
      ...(providerType === "saml" ? { samlSsoUrl, samlCert } : {}),
    }),
  });

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/chatbots/${selectedBotId}/external-idp`, buildPayload());
      if (!res.ok) throw new Error(await res.text());
      return res;
    },
    onSuccess: () => {
      toast({ title: "External SSO configuration saved." });
      queryClient.invalidateQueries({ queryKey: [`/api/chatbots/${selectedBotId}/external-idp`] });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/chatbots/${selectedBotId}/external-idp`);
      if (!res.ok) throw new Error(await res.text());
      return res;
    },
    onSuccess: () => {
      toast({ title: "External SSO configuration removed." });
      queryClient.invalidateQueries({ queryKey: [`/api/chatbots/${selectedBotId}/external-idp`] });
      // Reset to defaults
      setEnabled(true); setProviderType("oidc");
      setIssuer(""); setAudience(""); setClientId(""); setJwksUri("");
      setMetadataUrl(""); setSamlSsoUrl(""); setSamlCert("");
      setClaims({ ...DEFAULT_CLAIM_MAPPING });
      setTestResult(null);
    },
    onError: (e: Error) => {
      toast({ title: "Remove failed", description: e.message, variant: "destructive" });
    },
  });

  async function handleTestConnection() {
    if (!selectedBotId) return;
    setTestResult(null);
    setTestPending(true);
    try {
      const res = await apiRequest("POST", `/api/chatbots/${selectedBotId}/external-idp/test`, {
        providerType,
        issuer:      providerType === "oidc" ? issuer     : issuer,
        jwksUri:     providerType === "oidc" ? jwksUri    : undefined,
        metadataUrl: metadataUrl || undefined,
      });
      const body = await res.json() as { success: boolean; message: string };
      setTestResult(body);
    } catch {
      setTestResult({ success: false, message: "Request failed — check the browser console for details." });
    } finally {
      setTestPending(false);
    }
  }

  const hasExistingConfig = !!idpData;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Card data-testid="card-external-sso">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Network className="w-4 h-4" />
          External SSO <span className="text-xs font-normal text-muted-foreground">(Embedded Chatbot)</span>
        </CardTitle>
        <CardDescription>
          Allow embedded chatbots to authenticate users through an external Identity Provider
          without requiring a separate platform login. Configuration is per-chatbot.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">

        {/* ── Chatbot selector ─────────────────────────────────────────── */}
        <div className="space-y-1.5">
          <Label htmlFor="select-ext-sso-bot" className="flex items-center gap-1.5">
            <Bot className="w-3.5 h-3.5" />
            Chatbot
          </Label>
          {botsLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Select value={selectedBotId} onValueChange={v => { setSelectedBotId(v); }}>
              <SelectTrigger id="select-ext-sso-bot" data-testid="select-ext-sso-bot">
                <SelectValue placeholder="Select a chatbot to configure…" />
              </SelectTrigger>
              <SelectContent>
                {(bots as BotListItem[]).map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground">
            Each chatbot can have its own external IdP. Select a chatbot to view or edit its configuration.
          </p>
        </div>

        {/* ── Protocol status badge (shown once config is saved) ────────── */}
        {selectedBotId && hasExistingConfig && (
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium
              ${idpData?.enabled
                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                : "bg-muted text-muted-foreground"}`}
              data-testid="badge-ext-sso-status">
              <span className={`w-1.5 h-1.5 rounded-full ${idpData?.enabled ? "bg-green-500" : "bg-muted-foreground"}`} />
              {idpData?.providerType?.toUpperCase() ?? "OIDC"} — {idpData?.enabled ? "Active" : "Disabled"}
            </span>
          </div>
        )}

        {/* ── Per-bot form (only shown when a bot is selected) ─────────── */}
        {selectedBotId && (
          <>
            {idpLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
              </div>
            ) : (
              <div className="space-y-5 pt-1 border-t">

                {/* ── Enable toggle ─────────────────────────────────── */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="switch-ext-sso-enabled" className="text-sm font-medium">Enable External SSO</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When off, existing authentication behaviour for this chatbot remains unchanged.
                    </p>
                  </div>
                  <Switch
                    id="switch-ext-sso-enabled"
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    data-testid="switch-ext-sso-enabled"
                  />
                </div>

                {/* ── Provider type ──────────────────────────────────── */}
                <div className="space-y-1.5">
                  <Label htmlFor="select-ext-sso-provider">Authentication Protocol</Label>
                  <Select value={providerType} onValueChange={v => { setProviderType(v as "oidc" | "saml"); setTestResult(null); }}>
                    <SelectTrigger id="select-ext-sso-provider" data-testid="select-ext-sso-provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oidc">OIDC (OpenID Connect)</SelectItem>
                      <SelectItem value="saml">SAML 2.0</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* ── OIDC fields ────────────────────────────────────── */}
                {providerType === "oidc" && (
                  <div className="space-y-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">OIDC Configuration</p>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-issuer">Issuer URL <span className="text-destructive">*</span></Label>
                      <Input
                        id="input-ext-issuer"
                        value={issuer}
                        onChange={e => setIssuer(e.target.value)}
                        placeholder="https://accounts.example.com"
                        data-testid="input-ext-issuer"
                      />
                      <p className="text-xs text-muted-foreground">The exact issuer value from the provider's JWT tokens and discovery document.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-audience">Audience</Label>
                      <Input
                        id="input-ext-audience"
                        value={audience}
                        onChange={e => setAudience(e.target.value)}
                        placeholder="talentiq-chatbot"
                        data-testid="input-ext-audience"
                      />
                      <p className="text-xs text-muted-foreground">Expected <code className="text-xs bg-muted px-1 rounded">aud</code> claim value. Leave blank to skip audience validation.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-client-id">Client ID</Label>
                      <Input
                        id="input-ext-client-id"
                        value={clientId}
                        onChange={e => setClientId(e.target.value)}
                        placeholder="my-app-client-id"
                        data-testid="input-ext-client-id"
                      />
                      <p className="text-xs text-muted-foreground">Optional. Used for display and documentation — token validation uses JWKS, not client credentials.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-jwks-uri">JWKS URL</Label>
                      <Input
                        id="input-ext-jwks-uri"
                        value={jwksUri}
                        onChange={e => setJwksUri(e.target.value)}
                        placeholder="https://accounts.example.com/.well-known/jwks.json"
                        data-testid="input-ext-jwks-uri"
                      />
                      <p className="text-xs text-muted-foreground">Direct JWKS endpoint. If blank, the system derives it from the Issuer via OIDC discovery.</p>
                    </div>
                  </div>
                )}

                {/* ── SAML fields ────────────────────────────────────── */}
                {providerType === "saml" && (
                  <div className="space-y-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">SAML 2.0 Configuration</p>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-saml-metadata">Metadata URL</Label>
                      <Input
                        id="input-ext-saml-metadata"
                        value={metadataUrl}
                        onChange={e => setMetadataUrl(e.target.value)}
                        placeholder="https://idp.example.com/metadata.xml"
                        data-testid="input-ext-saml-metadata"
                      />
                      <p className="text-xs text-muted-foreground">IdP federation metadata XML URL. Used for Test Connection validation.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-saml-entity-id">Entity ID</Label>
                      <Input
                        id="input-ext-saml-entity-id"
                        value={issuer}
                        onChange={e => setIssuer(e.target.value)}
                        placeholder="https://idp.example.com/saml"
                        data-testid="input-ext-saml-entity-id"
                      />
                      <p className="text-xs text-muted-foreground">The IdP Entity ID from the federation metadata.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="input-ext-saml-sso-url">SSO URL</Label>
                      <Input
                        id="input-ext-saml-sso-url"
                        value={samlSsoUrl}
                        onChange={e => setSamlSsoUrl(e.target.value)}
                        placeholder="https://idp.example.com/sso/saml"
                        data-testid="input-ext-saml-sso-url"
                      />
                      <p className="text-xs text-muted-foreground">The IdP's Single Sign-On service URL.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="textarea-ext-saml-cert">Certificate (PEM)</Label>
                      <Textarea
                        id="textarea-ext-saml-cert"
                        value={samlCert ? "••••••••••••••••••••" : ""}
                        onFocus={e => { if (samlCert) (e.target as HTMLTextAreaElement).value = samlCert; }}
                        onBlur={e => {
                          const val = (e.target as HTMLTextAreaElement).value;
                          if (val && !val.startsWith("•")) setSamlCert(val);
                          else if (val.startsWith("•")) (e.target as HTMLTextAreaElement).value = "••••••••••••••••••••";
                        }}
                        onChange={e => {
                          const val = e.target.value;
                          if (!val.startsWith("•")) setSamlCert(val);
                        }}
                        placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                        className="font-mono text-xs min-h-[80px]"
                        data-testid="textarea-ext-saml-cert"
                      />
                      <p className="text-xs text-muted-foreground">Public X.509 certificate from the IdP metadata for assertion signature verification.</p>
                    </div>

                    <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30 p-3 text-xs text-blue-700 dark:text-blue-300 flex gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>SAML 2.0 token validation is fully supported. The platform validates assertion signatures, issuer, audience, and expiry before extracting user claims.</span>
                    </div>
                  </div>
                )}

                {/* ── Claim mapping ──────────────────────────────────── */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium">User Identity Mapping</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Map JWT claim names to platform identity fields. Leave as default if your provider uses standard OIDC claim names.</p>
                  </div>

                  {(
                    [
                      { key: "userIdClaimName",     label: "User ID Claim",     placeholder: "sub",        testId: "input-ext-claim-userid"  },
                      { key: "emailClaimName",       label: "Email Claim",       placeholder: "email",      testId: "input-ext-claim-email"   },
                      { key: "nameClaimName",        label: "Name Claim",        placeholder: "name",       testId: "input-ext-claim-name"    },
                      { key: "roleClaimName",        label: "Role Claim",        placeholder: "roles",      testId: "input-ext-claim-role"    },
                      { key: "departmentClaimName",  label: "Department Claim",  placeholder: "department", testId: "input-ext-claim-dept"    },
                      { key: "groupsClaimName",      label: "Groups Claim",      placeholder: "groups",     testId: "input-ext-claim-groups"  },
                    ] as const
                  ).map(({ key, label, placeholder, testId }) => (
                    <div key={key} className="grid grid-cols-2 gap-3 items-center">
                      <Label htmlFor={testId} className="text-xs text-muted-foreground">{label}</Label>
                      <Input
                        id={testId}
                        value={claims[key]}
                        onChange={e => setClaims(c => ({ ...c, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="text-xs h-8"
                        data-testid={testId}
                      />
                    </div>
                  ))}
                </div>

                {/* ── Test Connection ────────────────────────────────── */}
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testPending || (!issuer && !jwksUri && !metadataUrl)}
                    data-testid="button-ext-sso-test"
                  >
                    {testPending
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <RefreshCw className="w-4 h-4 mr-2" />}
                    {testPending ? "Testing…" : "Test Connection"}
                  </Button>

                  {testResult && (
                    <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                      testResult.success
                        ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
                        : "border-destructive/30 bg-destructive/5 text-destructive"
                    }`} data-testid="ext-sso-test-result">
                      {testResult.success
                        ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                        : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                      <span>{testResult.message}</span>
                    </div>
                  )}
                </div>

                {/* ── Action buttons ─────────────────────────────────── */}
                <div className="flex items-center gap-3 pt-1">
                  <Button
                    onClick={() => save.mutate()}
                    disabled={save.isPending}
                    data-testid="button-ext-sso-save"
                  >
                    {save.isPending ? "Saving…" : "Save Configuration"}
                  </Button>

                  {hasExistingConfig && (
                    <Button
                      variant="outline"
                      onClick={() => remove.mutate()}
                      disabled={remove.isPending}
                      className="text-destructive border-destructive/40 hover:bg-destructive/10"
                      data-testid="button-ext-sso-delete"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {remove.isPending ? "Removing…" : "Remove Configuration"}
                    </Button>
                  )}
                </div>

              </div>
            )}
          </>
        )}

      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SsoSettingsPage() {
  const { toast } = useToast();
  const brandShort = useBranding().shortName;
  const hasScim = useFeature("scim");
  const { data, isLoading } = useQuery<SsoResponse>({ queryKey: ["/api/org/sso"] });
  // Fix: backend is at /api/scim-tokens (not /api/org/scim-tokens)
  const { data: tokens } = useQuery<ScimToken[]>({ queryKey: ["/api/scim-tokens"], enabled: hasScim });
  const { data: scimConfig } = useQuery<ScimConfig>({ queryKey: ["/api/organization/scim-config"], enabled: hasScim });

  const [mode, setMode] = useState("public");
  const [ssoEnforced, setSsoEnforced] = useState(false);
  const [provider, setProvider] = useState<"oidc" | "saml" | "test">("test");
  const [oidc, setOidc] = useState({ issuer: "", clientId: "", clientSecret: "", scopes: "openid email profile", emailAttr: "email", nameAttr: "name", deptAttr: "department", titleAttr: "title", managerAttr: "" });
  const [saml, setSaml] = useState({ entryPoint: "", issuer: "", cert: "" });
  // Role mapping: array of {idpRole, internalRole} pairs for the UI.
  // Serialised as a Record<string,string> when sent to the API.
  const [roleMappingRows, setRoleMappingRows] = useState<{ idpRole: string; internalRole: string }[]>([]);
  const [defaultSsoRole, setDefaultSsoRole] = useState("Member");
  const [tokenLabel, setTokenLabel] = useState("");
  const [newToken, setNewToken] = useState<string | null>(null);
  const [rotatedTokenId, setRotatedTokenId] = useState<string | null>(null);
  const [rotatedTokenValue, setRotatedTokenValue] = useState<string | null>(null);
  // SCIM config local state
  const [scimDefaultRole, setScimDefaultRole] = useState("");
  const [scimGroupMapping, setScimGroupMapping] = useState("");
  const [scimDomain, setScimDomain] = useState("");

  useEffect(() => {
    if (data) {
      setMode(data.mode || "public");
      setSsoEnforced(data.ssoEnforced === "true");
      const cfg = data.config || {};
      if (cfg.oidc?.issuer) setProvider("oidc");
      else if (cfg.saml?.entryPoint) setProvider("saml");
      else setProvider("test");
      setOidc({
        issuer: cfg.oidc?.issuer || "",
        clientId: cfg.oidc?.clientId || "",
        clientSecret: cfg.oidc?.clientSecret || "",
        scopes: cfg.oidc?.scopes || "openid email profile",
        emailAttr: cfg.oidc?.attributeMapping?.email || "email",
        nameAttr: cfg.oidc?.attributeMapping?.name || "name",
        deptAttr: cfg.oidc?.attributeMapping?.department || "department",
        titleAttr: cfg.oidc?.attributeMapping?.jobTitle || "title",
        managerAttr: cfg.oidc?.attributeMapping?.manager || "",
      });
      setSaml({ entryPoint: cfg.saml?.entryPoint || "", issuer: cfg.saml?.issuer || "", cert: cfg.saml?.cert || "" });
      // Hydrate role mapping from server response.
      const rm = cfg.roleMapping ?? {};
      setRoleMappingRows(Object.entries(rm).map(([idpRole, internalRole]) => ({ idpRole, internalRole })));
      setDefaultSsoRole(cfg.defaultRole || "Member");
    }
  }, [data]);

  useEffect(() => {
    if (scimConfig) {
      setScimDefaultRole(scimConfig.defaultRole || "");
      setScimGroupMapping(scimConfig.groupAttributeMapping || "");
      setScimDomain(scimConfig.domainRestriction || "");
    }
  }, [scimConfig]);

  const save = useMutation({
    mutationFn: async () => {
      // Always send the chosen provider explicitly so the server doesn't fall
      // back to a different one based on which sub-config blocks happen to be
      // populated. This is important for the Test IdP path where the provider
      // has no extra config of its own.
      const config: SsoConfig = { provider };
      if (provider === "oidc") {
        config.oidc = {
          issuer: oidc.issuer, clientId: oidc.clientId, clientSecret: oidc.clientSecret, scopes: oidc.scopes,
          attributeMapping: { email: oidc.emailAttr, name: oidc.nameAttr, department: oidc.deptAttr, jobTitle: oidc.titleAttr, manager: oidc.managerAttr || undefined },
        };
      } else if (provider === "saml") {
        config.saml = saml;
      }
      // Convert role mapping rows → Record<string,string>, dropping blank entries.
      const roleMapping: Record<string, string> = {};
      for (const { idpRole, internalRole } of roleMappingRows) {
        if (idpRole.trim() && internalRole.trim()) roleMapping[idpRole.trim()] = internalRole.trim();
      }
      config.roleMapping = Object.keys(roleMapping).length > 0 ? roleMapping : undefined;
      config.defaultRole = defaultSsoRole !== "Member" ? defaultSsoRole : undefined;
      const res = await apiRequest("PUT", "/api/org/sso", { mode, ssoEnforced, config });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/org/sso"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "SSO settings saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const createToken = useMutation<{ id: string; label: string; token: string; createdAt: string }, Error>({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/scim-tokens", { label: tokenLabel });
      return res.json();
    },
    onSuccess: (t) => {
      setNewToken(t.token);
      setRotatedTokenId(null);
      setRotatedTokenValue(null);
      setTokenLabel("");
      queryClient.invalidateQueries({ queryKey: ["/api/scim-tokens"] });
      toast({ title: "Token created — copy it now, it won't be shown again." });
    },
    onError: (e) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const revokeToken = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/scim-tokens/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scim-tokens"] });
      toast({ title: "Token revoked" });
    },
  });

  const rotateToken = useMutation<{ id: string; label: string; token: string }, Error, string>({
    mutationFn: async (id) => {
      const res = await apiRequest("POST", `/api/scim-tokens/${id}/rotate`);
      return res.json();
    },
    onSuccess: (t) => {
      setRotatedTokenId(t.id);
      setRotatedTokenValue(t.token);
      setNewToken(null);
      queryClient.invalidateQueries({ queryKey: ["/api/scim-tokens"] });
      toast({ title: "Token rotated — old token is immediately invalid. Copy the new token now." });
    },
    onError: (e) => toast({ title: "Rotation failed", description: e.message, variant: "destructive" }),
  });

  const saveScimConfig = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/organization/scim-config", {
        DefaultRole: scimDefaultRole || null,
        GroupAttributeMapping: scimGroupMapping || null,
        DomainRestriction: scimDomain || null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/scim-config"] });
      toast({ title: "SCIM configuration saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <div className="p-6"><Skeleton className="h-96 w-full" /></div>;

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="heading-sso">
          <Shield className="w-6 h-6" /> Identity & SSO
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Configure single sign-on, deployment mode, and SCIM provisioning.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deployment Mode</CardTitle>
          <CardDescription>Internal mode hides public marketing/registration pages and is intended for staff-only deployments.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="select-mode">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger id="select-mode" data-testid="select-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public SaaS (anyone can register)</SelectItem>
                <SelectItem value="internal">Internal-only (staff via SSO)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <p className="text-sm font-medium" id="switch-enforce-sso-label">Enforce SSO sign-in</p>
              <p className="text-xs text-muted-foreground">When on, password login is disabled for org members. Super admins remain able to sign in with passwords.</p>
            </div>
            <Switch checked={ssoEnforced} onCheckedChange={setSsoEnforced} aria-labelledby="switch-enforce-sso-label" data-testid="switch-enforce-sso" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Key className="w-4 h-4" />SSO Provider</CardTitle>
          <CardDescription>Pick a provider type and enter the connection details from your IdP.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={provider} onValueChange={v => setProvider(v as "oidc" | "saml" | "test")}>
            <TabsList>
              <TabsTrigger value="oidc" data-testid="tab-oidc">OIDC</TabsTrigger>
              <TabsTrigger value="saml" data-testid="tab-saml">SAML</TabsTrigger>
              <TabsTrigger value="test" data-testid="tab-test">Test IdP</TabsTrigger>
            </TabsList>

            <TabsContent value="oidc" className="space-y-3 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="input-oidc-issuer">Issuer URL</Label>
                  <Input id="input-oidc-issuer" value={oidc.issuer} onChange={e => setOidc({ ...oidc, issuer: e.target.value })} placeholder="https://login.microsoftonline.com/{tenant}/v2.0" data-testid="input-oidc-issuer" />
                </div>
                <div>
                  <Label htmlFor="input-oidc-scopes">Scopes</Label>
                  <Input id="input-oidc-scopes" value={oidc.scopes} onChange={e => setOidc({ ...oidc, scopes: e.target.value })} data-testid="input-oidc-scopes" />
                </div>
                <div>
                  <Label htmlFor="input-oidc-client-id">Client ID</Label>
                  <Input id="input-oidc-client-id" value={oidc.clientId} onChange={e => setOidc({ ...oidc, clientId: e.target.value })} data-testid="input-oidc-client-id" />
                </div>
                <div>
                  <Label htmlFor="input-oidc-client-secret">Client Secret</Label>
                  <Input id="input-oidc-client-secret" type="password" value={oidc.clientSecret} onChange={e => setOidc({ ...oidc, clientSecret: e.target.value })} placeholder="(unchanged)" data-testid="input-oidc-client-secret" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Attribute Mapping</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <div><Label className="text-xs" htmlFor="input-oidc-email-attr">Email claim</Label><Input id="input-oidc-email-attr" value={oidc.emailAttr} onChange={e => setOidc({ ...oidc, emailAttr: e.target.value })} data-testid="input-oidc-email-attr" /></div>
                  <div><Label className="text-xs" htmlFor="input-oidc-name-attr">Name claim</Label><Input id="input-oidc-name-attr" value={oidc.nameAttr} onChange={e => setOidc({ ...oidc, nameAttr: e.target.value })} data-testid="input-oidc-name-attr" /></div>
                  <div><Label className="text-xs" htmlFor="input-oidc-dept-attr">Department claim</Label><Input id="input-oidc-dept-attr" value={oidc.deptAttr} onChange={e => setOidc({ ...oidc, deptAttr: e.target.value })} data-testid="input-oidc-dept-attr" /></div>
                  <div><Label className="text-xs" htmlFor="input-oidc-title-attr">Job title claim</Label><Input id="input-oidc-title-attr" value={oidc.titleAttr} onChange={e => setOidc({ ...oidc, titleAttr: e.target.value })} data-testid="input-oidc-title-attr" /></div>
                  <div><Label className="text-xs" htmlFor="input-oidc-manager-attr">Manager email claim (optional)</Label><Input id="input-oidc-manager-attr" value={oidc.managerAttr} placeholder="manager" onChange={e => setOidc({ ...oidc, managerAttr: e.target.value })} data-testid="input-oidc-manager-attr" /></div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="saml" className="space-y-3 pt-4">
              <div>
                <Label htmlFor="input-saml-entry">SSO Entry Point (IdP SSO URL)</Label>
                <Input id="input-saml-entry" value={saml.entryPoint} onChange={e => setSaml({ ...saml, entryPoint: e.target.value })} placeholder="https://idp.example.com/saml/sso" data-testid="input-saml-entry" />
              </div>
              <div>
                <Label htmlFor="input-saml-issuer">Issuer / Audience (SP entityID)</Label>
                <Input id="input-saml-issuer" value={saml.issuer} onChange={e => setSaml({ ...saml, issuer: e.target.value })} placeholder="urn:bot:sp" data-testid="input-saml-issuer" />
              </div>
              <div>
                <Label htmlFor="input-saml-cert">X.509 Certificate (PEM, from IdP metadata)</Label>
                <textarea
                  id="input-saml-cert"
                  value={saml.cert}
                  onChange={e => setSaml({ ...saml, cert: e.target.value })}
                  className="w-full min-h-[140px] font-mono text-xs border rounded-md p-3 bg-background"
                  placeholder="-----BEGIN CERTIFICATE-----"
                  data-testid="input-saml-cert"
                />
              </div>
              <p className="text-xs text-muted-foreground">After saving, give your IdP the ACS URL shown below. Assertions must be signed.</p>
            </TabsContent>

            <TabsContent value="test" className="space-y-3 pt-4">
              <div className="rounded-md border bg-muted/30 p-4">
                <p className="text-sm font-medium mb-2">Test IdP (built-in)</p>
                <p className="text-xs text-muted-foreground">A built-in mock IdP for development & E2E testing. It signs users in by email; useful before connecting a real IdP. No additional configuration required.</p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium">Service Provider URLs (give these to your IdP)</p>
            {data && (
              <>
                <CopyField label="Sign-in URL (start here)" value={`${origin}${data.callbackUrls.loginUrl}`} testId="input-login-url" />
                <CopyField label="OIDC Redirect URI" value={`${origin}${data.callbackUrls.oidc}`} testId="input-oidc-callback" />
                <CopyField label="SAML ACS URL" value={`${origin}${data.callbackUrls.saml}`} testId="input-saml-callback" />
              </>
            )}
          </div>

          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-sso">
            {save.isPending ? "Saving..." : "Save SSO settings"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Lock className="w-4 h-4" />SCIM Provisioning Tokens</CardTitle>
          <CardDescription>Create bearer tokens that your IdP can use to push users and groups to {brandShort} via SCIM 2.0.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="input-token-label">Token label</Label>
              <Input id="input-token-label" value={tokenLabel} onChange={e => setTokenLabel(e.target.value)} placeholder="e.g. Okta production" data-testid="input-token-label" />
            </div>
            <Button onClick={() => createToken.mutate()} disabled={!tokenLabel || createToken.isPending} data-testid="button-create-token">
              <Plus className="w-4 h-4 mr-1" />Create
            </Button>
          </div>

          {newToken && (
            <div className="rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 p-3">
              <p className="text-xs font-medium flex items-center gap-1 text-amber-900 dark:text-amber-200"><AlertTriangle className="w-3.5 h-3.5" />Copy this token now — it will not be shown again.</p>
              <Input readOnly value={newToken} className="mt-2 font-mono text-xs" data-testid="input-new-token" />
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setNewToken(null)} data-testid="button-dismiss-token">Done</Button>
            </div>
          )}

          <div>
            <Label className="text-xs" htmlFor="input-scim-url">SCIM Base URL</Label>
            <Input readOnly id="input-scim-url" value={`${origin}/scim/v2`} className="font-mono text-xs" data-testid="input-scim-url" />
          </div>

          {rotatedTokenValue && (
            <div className="rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 p-3">
              <p className="text-xs font-medium flex items-center gap-1 text-amber-900 dark:text-amber-200"><AlertTriangle className="w-3.5 h-3.5" />Token rotated — old token is immediately invalid. Copy the new token now.</p>
              <Input readOnly value={rotatedTokenValue} className="mt-2 font-mono text-xs" data-testid="input-rotated-token" />
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => { setRotatedTokenId(null); setRotatedTokenValue(null); }} data-testid="button-dismiss-rotated-token">Done</Button>
            </div>
          )}

          <div className="border rounded-md divide-y">
            {!tokens || tokens.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">No SCIM tokens issued yet.</p>
            ) : (
              tokens.map(t => (
                <div key={t.id} className="flex items-center justify-between p-3" data-testid={`token-row-${t.id}`}>
                  <div>
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(t.createdAt).toLocaleDateString()}
                      {t.lastUsedAt && ` · Last used ${new Date(t.lastUsedAt).toLocaleString()}`}
                      {!t.lastUsedAt && " · Never used"}
                    </p>
                    {rotatedTokenId === t.id && !rotatedTokenValue && (
                      <Badge variant="outline" className="mt-1 text-[10px]">Rotated</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <IconButton variant="ghost" label={`Rotate token ${t.label}`} onClick={() => rotateToken.mutate(t.id)} data-testid={`button-rotate-token-${t.id}`}>
                      <RefreshCw className="w-4 h-4" aria-hidden="true" />
                    </IconButton>
                    <IconButton variant="ghost" label={`Revoke token ${t.label}`} onClick={() => revokeToken.mutate(t.id)} data-testid={`button-revoke-token-${t.id}`}>
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </IconButton>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role Mapping — available when any SSO provider is configured */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4" />SSO Role Mapping</CardTitle>
          <CardDescription>
            Map IdP group or role claim values to internal platform roles. When the IdP sends a role
            claim (configured in your provider's attribute mapping above), users are automatically
            assigned the matching internal role on each sign-in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="select-default-sso-role">Default role (no matching claim)</Label>
            <Select value={defaultSsoRole} onValueChange={setDefaultSsoRole}>
              <SelectTrigger id="select-default-sso-role" data-testid="select-default-sso-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Member">Member</SelectItem>
                <SelectItem value="OrgAdmin">OrgAdmin</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Applied when the IdP sends no role claim, or the claim doesn't match any row below.</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Claim value → internal role</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRoleMappingRows(prev => [...prev, { idpRole: "", internalRole: "Member" }])}
                data-testid="button-add-role-mapping"
              >
                <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Add mapping
              </Button>
            </div>

            {roleMappingRows.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                No mappings configured — all SSO users receive the default role above.
              </p>
            ) : (
              <div className="space-y-2">
                {roleMappingRows.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      placeholder="IdP claim value (e.g. Admins)"
                      value={row.idpRole}
                      onChange={e => setRoleMappingRows(prev =>
                        prev.map((r, i) => i === idx ? { ...r, idpRole: e.target.value } : r)
                      )}
                      className="flex-1 text-sm"
                      aria-label={`IdP role value for mapping row ${idx + 1}`}
                    />
                    <span className="text-muted-foreground text-sm shrink-0">→</span>
                    <Select
                      value={row.internalRole}
                      onValueChange={v => setRoleMappingRows(prev =>
                        prev.map((r, i) => i === idx ? { ...r, internalRole: v } : r)
                      )}
                    >
                      <SelectTrigger className="w-36" aria-label={`Internal role for mapping row ${idx + 1}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Member">Member</SelectItem>
                        <SelectItem value="OrgAdmin">OrgAdmin</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setRoleMappingRows(prev => prev.filter((_, i) => i !== idx))}
                      aria-label={`Remove mapping row ${idx + 1}`}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              The role claim attribute name is set in your provider's Attribute Mapping tab (the "role" field).
              Blank or duplicate IdP values are ignored on save.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── External SSO (Embedded Chatbot) — per-bot external IdP ────────────── */}
      {/* Completely separate from org-level SSO above. Does not touch existing  */}
      {/* TalentIQ login, OIDC/SAML org SSO, or SCIM configuration.             */}
      <ExternalSsoSection />

      {hasScim && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Settings2 className="w-4 h-4" />SCIM Provisioning Settings</CardTitle>
            <CardDescription>Configure how SCIM-provisioned users and groups are handled in {brandShort}.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="select-scim-default-role">Default role for new SCIM users</Label>
              <Select value={scimDefaultRole || "Member"} onValueChange={setScimDefaultRole}>
                <SelectTrigger id="select-scim-default-role" data-testid="select-scim-default-role">
                  <SelectValue placeholder="Member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="OrgAdmin">OrgAdmin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Role assigned to users when no role attribute is present in the SCIM payload.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="input-scim-domain">Domain restriction</Label>
              <Input
                id="input-scim-domain"
                value={scimDomain}
                onChange={e => setScimDomain(e.target.value)}
                placeholder="university.edu"
                data-testid="input-scim-domain"
              />
              <p className="text-xs text-muted-foreground">If set, only email addresses with this domain may be provisioned. Leave blank to allow all domains.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="textarea-scim-group-mapping">Group attribute mapping (JSON)</Label>
              <Textarea
                id="textarea-scim-group-mapping"
                value={scimGroupMapping}
                onChange={e => setScimGroupMapping(e.target.value)}
                placeholder={'{"IT Staff": "OrgAdmin", "Employees": "Member"}'}
                className="font-mono text-xs min-h-[80px]"
                data-testid="textarea-scim-group-mapping"
              />
              <p className="text-xs text-muted-foreground">Maps IdP group names to platform roles. Leave blank to sync groups without automatic role assignment.</p>
            </div>

            <Button onClick={() => saveScimConfig.mutate()} disabled={saveScimConfig.isPending} data-testid="button-save-scim-config">
              {saveScimConfig.isPending ? "Saving..." : "Save SCIM settings"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
